import type { ChecklistItem } from "@hamecho/content-schema";
import { $applyNodeReplacement, type LexicalNode, type NodeKey, type SerializedLexicalNode, Spread } from "lexical";
import { GripVertical, ListTodo, Plus, Save, Trash2 } from "lucide-react";
import { JSX, useState } from "react";
import { SaveAsTemplateDialog } from "@/components/Checklist/SaveAsTemplateDialog";
import { TemplateSelectDialog } from "@/components/Checklist/TemplateSelectDialog";
import { NodeDeleteButton } from "@/components/Editor/NodeDeleteButton";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { useTranslate } from "@/utils/i18n";
import { BaseAppNode, registerBaseAppNodeType } from "./BaseAppNode";
import { WindowDecorator } from "./WindowNode";

// Register this node type with BaseAppNode
registerBaseAppNodeType("checklist");

export type SerializedChecklistNode = Spread<
  {
    type: "checklist";
    version: 1;
    key: string;
    items: ChecklistItem[];
    title?: string;
    templateId?: string;
  },
  SerializedLexicalNode
>;

/**
 * ChecklistNode - 检查清单节点
 *
 * 支持分类、完成状态跟踪的检查清单
 * 支持模板应用和保存
 *
 * Per architecture: Node holds value fields directly. Content Schema is SSOT via Adapter.
 */
export class ChecklistNode extends BaseAppNode {
  __items: ChecklistItem[];
  __title?: string;
  __templateId?: string;

  static getType(): string {
    return "checklist";
  }

  static clone(node: ChecklistNode): ChecklistNode {
    return new ChecklistNode(node.__items, node.__title, node.__templateId, node.__key);
  }

  constructor(items: ChecklistItem[] = [], title?: string, templateId?: string, key?: NodeKey) {
    super(false, key); // false = block-level node
    this.__items = items;
    this.__title = title;
    this.__templateId = templateId;
  }

  // ========== DOM 操作 ==========

  createDOM(): HTMLElement {
    const div = document.createElement("div");
    div.className = "checklist-node-container";
    return div;
  }

  updateDOM(): boolean {
    return false;
  }

  decorate(): JSX.Element {
    return <ChecklistDecorator node={this} />;
  }

  // ========== 序列化 ==========

  static importJSON(serializedNode: SerializedChecklistNode): ChecklistNode {
    const node = $createChecklistNode(serializedNode.items || [], serializedNode.title, serializedNode.templateId);
    return node;
  }

  exportJSON(): SerializedChecklistNode {
    return {
      type: "checklist",
      version: 1,
      key: this.__key,
      items: this.__items,
      title: this.__title,
    };
  }

  // ========== Getter/Setter ==========

  getItems(): ChecklistItem[] {
    return this.__items;
  }

  setItems(items: ChecklistItem[]): void {
    const writable = this.getWritable();
    writable.__items = items;
  }

  addItem(item: ChecklistItem): void {
    const writable = this.getWritable();
    writable.__items = [...writable.__items, item];
  }

  updateItem(index: number, updates: Partial<ChecklistItem>): void {
    const writable = this.getWritable();
    writable.__items = writable.__items.map((item, i) => (i === index ? { ...item, ...updates } : item));
  }

  removeItem(index: number): void {
    const writable = this.getWritable();
    writable.__items = writable.__items.filter((_, i) => i !== index);
  }

  getTitle(): string {
    return this.__title || "";
  }

  setTitle(title: string): void {
    const writable = this.getWritable();
    writable.__title = title;
  }

  getTemplateId(): string {
    return this.__templateId || "";
  }

  setTemplateId(templateId: string): void {
    const writable = this.getWritable();
    writable.__templateId = templateId;
  }

  // ========== 其他 ==========

  getTextContent(): string {
    const checkedCount = this.__items.filter((item) => item.checked).length;
    return `[检查清单] ${this.__title || "未命名"} (${checkedCount}/${this.__items.length})`;
  }

  isEqualTo(other: LexicalNode): boolean {
    if (!(other instanceof ChecklistNode)) {
      return false;
    }
    return this.__title === other.__title && this.__items.length === other.__items.length;
  }
}

/**
 * 检查清单节点编辑器组件
 */
function ChecklistDecorator({ node }: { node: ChecklistNode }) {
  const t = useTranslate();
  const [items, setItems] = useState(node.getItems());
  const [title, setTitle] = useState(node.getTitle());
  const [newItemText, setNewItemText] = useState("");
  const [newItemCategory, setNewItemCategory] = useState("");
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);

  const handleAddItem = () => {
    if (newItemText.trim()) {
      const newItem: ChecklistItem = {
        id: crypto.randomUUID(),
        text: newItemText.trim(),
        checked: false,
        category: newItemCategory.trim() || undefined,
      };
      const updated = [...items, newItem];
      setItems(updated);
      node.addItem(newItem);
      setNewItemText("");
      setNewItemCategory("");
    }
  };

  const handleToggleItem = (index: number) => {
    const updated = items.map((item, i) => (i === index ? { ...item, checked: !item.checked } : item));
    setItems(updated);
    node.updateItem(index, { checked: !items[index].checked });
  };

  const handleUpdateItemText = (index: number, text: string) => {
    const updated = items.map((item, i) => (i === index ? { ...item, text } : item));
    setItems(updated);
    node.updateItem(index, { text });
  };

  const handleRemoveItem = (index: number) => {
    const updated = items.filter((_, i) => i !== index);
    setItems(updated);
    node.removeItem(index);
  };

  const handleApplyTemplate = (templateItems: ChecklistItem[]) => {
    // Replace current items with template items
    setItems(templateItems);
    node.setItems(templateItems);
  };

  const completedCount = items.filter((item) => item.checked).length;

  return (
    <WindowDecorator node={node} className="my-4 p-4 rounded-lg border bg-card shadow-sm group">
      {(handleDelete) => (
        <>
          {/* 标题和工具栏 */}
          <div className="flex items-center justify-between mb-4">
            <Input
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                node.setTitle(e.target.value);
              }}
              placeholder={t("editor.checklistDetail.title-placeholder" as any)}
              className="text-lg font-bold border-none p-0 focus-visible:ring-0 flex-1"
            />
            <div className="flex items-center gap-2">
              <div className="text-sm text-muted-foreground mr-2">
                {completedCount}/{items.length} {t("editor.checklistDetail.completed" as any)}
              </div>

              <TemplateSelectDialog open={isTemplateDialogOpen} onOpenChange={setIsTemplateDialogOpen} onApply={handleApplyTemplate} />

              <SaveAsTemplateDialog
                trigger={
                  <Button variant="outline" size="sm" title={t("editor.checklistDetail.saveAsTemplate" as any)}>
                    <Save className="w-4 h-4" />
                  </Button>
                }
                items={items}
              />

              <Button
                variant="outline"
                size="sm"
                title={t("editor.checklistDetail.applyTemplate" as any)}
                onClick={() => setIsTemplateDialogOpen(true)}
              >
                <ListTodo className="w-4 h-4" />
              </Button>

              <NodeDeleteButton onClick={handleDelete} title={t("editor.checklistDetail.deleteChecklist" as any)} variant="hover-show" />
            </div>
          </div>

          {/* 进度条 */}
          {items.length > 0 && (
            <div className="mb-4">
              <div className="w-full bg-secondary rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all"
                  style={{ width: `${items.length > 0 ? (completedCount / items.length) * 100 : 0}%` }}
                />
              </div>
            </div>
          )}

          {/* 清单项列表 */}
          <div className="space-y-2 mb-4">
            {items.map((item, index) => (
              <div
                key={index}
                className="flex items-center gap-2 p-2 rounded-md bg-secondary/20 hover:bg-secondary/30 transition-colors group"
              >
                <GripVertical className="w-4 h-4 text-muted-foreground cursor-move" />
                <Checkbox checked={item.checked} onCheckedChange={() => handleToggleItem(index)} />
                <Input
                  value={item.text}
                  onChange={(e) => handleUpdateItemText(index, e.target.value)}
                  className={`flex-1 border-none p-0 focus-visible:ring-0 ${item.checked ? "line-through text-muted-foreground" : ""}`}
                />
                {item.category && <span className="text-xs px-2 py-1 rounded-md bg-muted text-muted-foreground">{item.category}</span>}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveItem(index)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>

          {/* 添加新项 */}
          <div className="space-y-2 pt-2 border-t">
            <Input
              value={newItemText}
              onChange={(e) => setNewItemText(e.target.value)}
              placeholder={t("editor.checklistDetail.itemPlaceholder" as any)}
              onKeyPress={(e) => e.key === "Enter" && handleAddItem()}
            />
            <div className="flex gap-2">
              <Input
                value={newItemCategory}
                onChange={(e) => setNewItemCategory(e.target.value)}
                placeholder={t("editor.checklistDetail.categoryPlaceholder" as any)}
                className="flex-1"
              />
              <Button onClick={handleAddItem} size="sm">
                <Plus className="w-4 h-4 mr-1" />
                {t("editor.checklistDetail.addItem" as any)}
              </Button>
            </div>
          </div>
        </>
      )}
    </WindowDecorator>
  );
}

/**
 * 创建 ChecklistNode
 */
export function $createChecklistNode(items: ChecklistItem[] = [], title?: string, templateId?: string): ChecklistNode {
  const node = new ChecklistNode(items, title, templateId);
  return $applyNodeReplacement(node);
}
